import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const PortalSwitcher = ({ className = '' }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { currentPortal, switchPortal, userProfile } = useAuth();

  const portals = [
    {
      id: 'admin',
      label: 'Admin Portal',
      icon: 'Shield',
      description: 'Full system access',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'merchant',
      label: 'Merchant Portal',
      icon: 'Store',
      description: 'Merchant operations',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    }
  ];

  const currentPortalData = portals?.find(p => p?.id === currentPortal) || portals?.[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handlePortalSwitch = (portalId) => {
    switchPortal(portalId);
    setDropdownOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-card transition-all duration-250"
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
      >
        <div className={`w-8 h-8 rounded-lg ${currentPortalData?.bgColor} ${currentPortalData?.color} flex items-center justify-center`}>
          <Icon name={currentPortalData?.icon} size={18} />
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-foreground leading-tight">
            {currentPortalData?.label}
          </div>
          <div className="text-xs text-muted-foreground">
            {currentPortalData?.description}
          </div>
        </div>
        <Icon 
          name="ChevronDown" 
          size={16} 
          className={`hidden md:block transition-transform duration-250 ${dropdownOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {dropdownOpen && (
        <div className="absolute left-0 top-full mt-2 w-64 bg-popover border border-border rounded-xl shadow-lg overflow-hidden z-50"
          style={{ animation: 'slideDown 200ms cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <div className="px-4 py-3 border-b border-border">
            <div className="text-sm font-medium text-popover-foreground">Switch Portal</div>
            <div className="text-xs text-muted-foreground mt-1">
              {userProfile?.role ? `Current role: ${userProfile?.role}` : 'Select your view'}
            </div>
          </div>
          
          {portals?.map((portal) => (
            <button
              key={portal?.id}
              onClick={() => handlePortalSwitch(portal?.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors duration-250 ${
                currentPortal === portal?.id ? 'bg-muted' : ''
              }`}
            >
              <div className={`w-10 h-10 rounded-lg ${portal?.bgColor} ${portal?.color} flex items-center justify-center flex-shrink-0`}>
                <Icon name={portal?.icon} size={20} />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-popover-foreground flex items-center gap-2">
                  {portal?.label}
                  {currentPortal === portal?.id && (
                    <Icon name="Check" size={16} className="text-primary" />
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {portal?.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalSwitcher;